import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { msalInstance, clearCache } from '../config/msalInstance';

const AuthContext = createContext(null);

const SESSION_COOKIE = 'fig_market_session';

const setSessionCookie = () => {
  document.cookie = `${SESSION_COOKIE}=1; path=/; SameSite=Lax; Secure`;
};

const hasSessionCookie = () =>
  document.cookie.split(';').some(c => c.trim().startsWith(`${SESSION_COOKIE}=`));

const clearSessionCookie = () => {
  document.cookie = `${SESSION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = sessionStorage.getItem('figMarketUser');
    if (stored && hasSessionCookie()) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        sessionStorage.removeItem('figMarketUser');
      }
    } else if (stored) {
      sessionStorage.clear();
    }
  }, []);

  const login = (userData) => {
    console.log('AuthContext.login:', userData);
    setUser(userData);
    sessionStorage.setItem('figMarketUser', JSON.stringify(userData));
    setSessionCookie();
  };

  const logout = async () => {
    const loginMethod = user?.loginMethod;
    const msalAccounts = msalInstance.getAllAccounts();
    setUser(null);
    sessionStorage.clear();
    clearSessionCookie();

    try {
      await clearCache();
    } catch (e) {
      console.error('clearCache error:', e);
    }

    // Only MSAL logout for Microsoft logins
    if (loginMethod === 'microsoft' && msalAccounts.length > 0) {
      try {
        await msalInstance.logoutRedirect({
          account: msalAccounts[0],
          postLogoutRedirectUri: window.location.origin + '/admin/login',
        });
        return;
      } catch (error) {
        console.error('MSAL logoutRedirect failed:', error);
      }
    }

    navigate('/admin/login', { replace: true });
  };

  const isSuperAdmin = () => user?.Role === 'SuperAdmin';
  const isStoreOwner = () => user?.Role === 'StoreOwner';
  const userStoreId = user?.StoreId || null;

  return (
    <AuthContext.Provider value={{
      user, login, logout, isAuthenticated: !!user, msalInstance,
      isSuperAdmin, isStoreOwner, userStoreId,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
